import { useEffect, useState } from 'react'
import { Plus, Loader2, Trash2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetUnits,
  useUpsertUnit,
  useDeleteUnit,
} from '../../hooks/backend/settingsPage'
import type { Unit } from '../../utils/units'
import { HelpText } from '../../components/HelpText'

const BASE_LABEL: Record<string, string> = {
  weight: 'g',
  volume: 'ml',
  count: 'each',
}
const TYPE_LABEL: Record<string, string> = {
  weight: 'Weight',
  volume: 'Volume',
  count: 'Count',
}

export default function UnitsSettings() {
  const getUnits = useGetUnits()
  const upsertUnit = useUpsertUnit()
  const deleteUnit = useDeleteUnit()

  const units = (getUnits.data as Unit[] | undefined) ?? []

  const [name, setName] = useState('')
  const [type, setType] = useState<'weight' | 'volume' | 'count'>('weight')
  const [toBase, setToBase] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    void getUnits.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refresh = () => getUnits.trigger({}, { skipCache: true })

  const add = async () => {
    const n = name.trim()
    const factor = Number(toBase)
    if (!n) {
      toast.error('Unit name is required')
      return
    }
    if (Number.isNaN(factor) || factor <= 0) {
      toast.error('Conversion factor must be a positive number')
      return
    }
    if (units.some(u => u.name.toLowerCase() === n.toLowerCase())) {
      toast.error(`"${n}" already exists`)
      return
    }
    try {
      setBusy('add')
      await upsertUnit.trigger({ name: n, unit_type: type, to_base: factor })
      setName('')
      setToBase('')
      await refresh()
    } catch {
      toast.error('Failed to add unit')
    } finally {
      setBusy(null)
    }
  }

  const remove = async (id: string) => {
    try {
      setBusy(id)
      await deleteUnit.trigger({ id })
      await refresh()
    } catch {
      toast.error('Failed to remove unit')
    } finally {
      setBusy(null)
    }
  }

  // Group units by type for display.
  const groups: { type: string; units: Unit[] }[] = []
  for (const u of units) {
    let g = groups.find(x => x.type === u.unit_type)
    if (!g) {
      g = { type: u.unit_type, units: [] }
      groups.push(g)
    }
    g.units.push(u)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Measurement Units</CardTitle>
        <CardDescription><HelpText>
          Units available in the ingredient and recipe pickers. The conversion factor
          is how many base units one of this unit equals (weight → grams, volume → ml,
          count → each). Costs convert automatically between units of the same type.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add form */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr,8rem,10rem,auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="unit-name">Name</Label>
            <Input
              id="unit-name"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
              placeholder="e.g. fl oz"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="unit-type">Type</Label>
            <Select value={type} onValueChange={v => setType(v as typeof type)}>
              <SelectTrigger id="unit-type" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weight">Weight</SelectItem>
                <SelectItem value="volume">Volume</SelectItem>
                <SelectItem value="count">Count</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="unit-factor">
              Factor to {BASE_LABEL[type]}
            </Label>
            <Input
              id="unit-factor"
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              value={toBase}
              onChange={e => setToBase(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
              placeholder="e.g. 29.57"
              className="h-9"
            />
          </div>
          <Button
            type="button"
            onClick={() => void add()}
            disabled={busy === 'add' || !name.trim() || !toBase.trim()}
            className="gap-1.5 shrink-0"
          >
            {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>

        {/* Existing units */}
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No units yet.</p>
        ) : (
          <div className="space-y-4">
            {groups.map(g => (
              <div key={g.type} className="space-y-1.5">
                <h4 className="text-sm font-semibold text-foreground">
                  {TYPE_LABEL[g.type] ?? g.type}
                </h4>
                <div className="divide-y rounded-md border">
                  {g.units.map(u => (
                    <div key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <span className="font-medium">{u.name}</span>
                        <span className="ml-2 text-muted-foreground">
                          1 {u.name} = {u.to_base} {BASE_LABEL[g.type] ?? ''}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => void remove(u.id)}
                        disabled={busy === u.id}
                        aria-label={`Remove ${u.name}`}
                      >
                        {busy === u.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
